export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-2 text-3xl font-bold">Terms of Use</h1>
        <p className="mb-1 text-sm text-muted-foreground">Effective date: July 29, 2026</p>
        <p className="mb-8 text-sm text-muted-foreground">Version 1.0</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground dark:prose-invert">
          <section>
            <p>
              Welcome to Stoop. These Terms of Use (&ldquo;Terms&rdquo;) are the agreement between you and Stoop for
              everyone who uses the app or website — buyers and sellers alike. They are written in plain English, but
              they are a binding legal agreement. Please read them.
            </p>
            <p>
              Stoop is operated by <strong>MarketLink Labs LLC</strong>, a New York limited liability company doing
              business as &ldquo;Stoop&rdquo; (&ldquo;Stoop&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
              &ldquo;our&rdquo;).
            </p>
            <p>
              <strong>By creating an account, or by browsing, buying, or otherwise using Stoop, you agree to these Terms
              and to our{" "}
              <a href="/privacy" className="underline">
                Privacy Policy
              </a>
              .</strong>{" "}
              If you sell on Stoop, the{" "}
              <a href="/terms" className="underline">
                Founding Maker Terms
              </a>{" "}
              also apply to you and add to these Terms. If you do not agree, do not use Stoop.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">1. What Stoop is</h2>
            <p>
              Stoop is an online local marketplace that connects neighborhood buyers with independent local sellers
              (&ldquo;Makers&rdquo;) of food and homemade goods. <strong>Stoop is a venue. We are not the seller, maker,
              producer, or merchant of record of the items listed.</strong> When you buy, you are entering a contract
              directly with the Maker, not with Stoop. We provide the platform, discovery, messaging, and payment
              processing that make the sale possible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">2. Eligibility</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You must be at least 18 years old to place an order or sell on Stoop. If you are between 13 and 18, you
                may use Stoop only with the involvement and consent of a parent or guardian who agrees to these Terms.
                Stoop is not directed to children under 13.
              </li>
              <li>You agree to provide accurate information and to keep your account details current.</li>
              <li>You are responsible for all activity under your account and for keeping your login secure.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">3. Orders, prices, and payments</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Makers set their own prices, descriptions, availability, lead times, and fulfillment options. Stoop
                shows you those details but does not set or guarantee them.
              </li>
              <li>
                <strong>Buyer service fee.</strong> Stoop charges buyers a service fee, shown at checkout before you
                pay. Some orders or items may also be subject to taxes, deposits (for made-to-order items), or delivery
                charges, all shown before you confirm.
              </li>
              <li>
                <strong>Payment.</strong> Payments are processed by Stripe. By placing an order, you authorize the
                charge shown at checkout. Stoop does not store your full card number.
              </li>
              <li>You agree to pay for orders you place and confirm.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">4. Fulfillment, pickup, and delivery</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                The Maker chooses how an order is fulfilled — pickup, meeting at a market, or delivery where available.
                Availability, timing, and any delivery area are set by the Maker and the platform and may change.
              </li>
              <li>
                Exact pickup details are shared with you only as needed to complete an order, and a Maker&rsquo;s home
                address is otherwise kept approximate for privacy.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">5. Refunds, cancellations, and disputes</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Each Maker sets and communicates their own refund and cancellation policy. Issues with an order —
                quality, timing, a missed pickup — are primarily between you and the Maker, who is responsible for the
                item.
              </li>
              <li>
                We may, but are not obligated to, help mediate. Refunds, where due, are processed through Stripe.
              </li>
              <li>
                <strong>Do not initiate a bank chargeback before contacting the Maker and us to resolve the issue.</strong>{" "}
                Fraudulent or bad-faith chargebacks may result in account suspension.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">6. Food safety, allergens, and assumption of risk</h2>
            <p>
              <strong>Please read this carefully.</strong> Items on Stoop are made by independent local Makers, often in
              home or cottage-food kitchens. <strong>Stoop does not make, inspect, test, or guarantee any item, and does
              not verify a Maker&rsquo;s licensing, ingredients, allergen handling, or food-safety practices.</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You are responsible for reviewing item descriptions and for asking the Maker about ingredients,
                allergens, cross-contamination, and preparation before you buy, especially if you have a food allergy or
                dietary restriction.
              </li>
              <li>
                To the fullest extent permitted by law, <strong>you assume the risks associated with buying and
                consuming food and homemade goods from independent Makers</strong>, and you agree that the Maker — not
                Stoop — is responsible for the safety, quality, and legality of what they sell.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">7. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>use Stoop for any unlawful purpose or to buy anything you are not legally allowed to buy;</li>
              <li>defraud, harass, threaten, or harm Makers, other users, or Stoop;</li>
              <li>post false, infringing, obscene, or misleading content, including in reviews or messages;</li>
              <li>scrape, overload, reverse-engineer, or attempt to break or circumvent the platform or its security.</li>
            </ul>
            <p>We may remove content and suspend or terminate accounts that violate these Terms or the law.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">8. Your content</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You may post content such as reviews, photos, and messages. You keep ownership of your content, and you
                grant Stoop a non-exclusive, royalty-free, worldwide license to host, display, and use it in connection
                with operating and promoting the platform.
              </li>
              <li>
                You are responsible for your content, you represent that you have the right to post it, and it must be
                honest and lawful.
              </li>
              <li>
                <strong>Feedback.</strong> If you send us ideas or suggestions about Stoop, you grant us a perpetual,
                royalty-free license to use them for any purpose without obligation to you.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">9. Intellectual property</h2>
            <p>
              The Stoop name, logo, app, and its design and content (other than user content) belong to Stoop and are
              protected by law. We grant you a limited, personal, non-transferable license to use the app for its
              intended purpose. You may not copy, modify, or distribute our content without permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">10. Privacy</h2>
            <p>
              Our handling of your personal information is described in our{" "}
              <a href="/privacy" className="underline">
                Privacy Policy
              </a>
              , which is incorporated into these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">11. Disclaimer of warranties</h2>
            <p>
              Stoop is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; and may include beta or
              still-developing features that can change or break. To the fullest extent permitted by law, we disclaim all
              warranties, express or implied, including merchantability, fitness for a particular purpose, title, and
              non-infringement. <strong>We do not warrant or guarantee any item sold by a Maker</strong>, or that the
              platform will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">12. Limitation of liability</h2>
            <p>To the fullest extent permitted by law:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Stoop is not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or
                for lost profits, data, or goodwill.
              </li>
              <li>
                <strong>Stoop&rsquo;s total aggregate liability</strong> for any and all claims arising out of or
                relating to these Terms, your use of the platform, or any order will not exceed the greater of (a) the
                total amount you paid to Stoop (in fees) or through Stoop on the order(s) giving rise to the claim in the
                6 months before it arose, or (b) $100.
              </li>
              <li>
                Because Stoop is a venue and not the seller, we are not responsible for the items themselves; claims
                about an item are against the Maker.
              </li>
              <li>
                These limits apply regardless of the theory of liability and even if a remedy fails of its essential
                purpose, and do not apply to liability that cannot be limited under law.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">13. Indemnification</h2>
            <p>
              You will indemnify and hold harmless Stoop and its operator, owners, successors, and assigns from any
              claims, damages, losses, and reasonable costs and attorneys&rsquo; fees arising out of your use of Stoop,
              your content, your violation of these Terms or any law, or your dispute with a Maker or other user. This
              survives termination.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">14. Dispute resolution, governing law, and waivers</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Governing law.</strong> These Terms are governed by the laws of the State of New York, without
                regard to its conflict-of-laws rules.
              </li>
              <li>
                <strong>Talk to us first.</strong> Before starting a formal proceeding, email{" "}
                <a href="mailto:hello@trystoop.com" className="underline">
                  hello@trystoop.com
                </a>{" "}
                and try in good faith to resolve the dispute informally for at least 30 days.
              </li>
              <li>
                <strong>Venue.</strong> If that fails, the dispute must be brought exclusively in the state courts
                located in Rensselaer County, New York, or in the U.S. District Court for the Northern District of New
                York, and you consent to that jurisdiction. Either party may still bring a qualifying claim in small
                claims court.
              </li>
              <li>
                <strong>Jury-trial waiver.</strong> To the extent permitted by law, you and Stoop each waive any right to
                a jury trial.
              </li>
              <li>
                <strong>Class-action waiver.</strong> To the extent permitted by law, disputes must be brought
                individually, not as part of any class, collective, or representative proceeding.
              </li>
              <li>
                <strong>Time limit.</strong> To the extent permitted by law, any claim must be filed within one year
                after it arises, or it is permanently barred.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">15. Termination</h2>
            <p>
              You may stop using Stoop and close your account at any time. We may suspend or terminate your access for
              violation of these Terms, legal or safety risk, or as required by law. Provisions that by their nature
              should survive — including payments owed, disclaimers, limitation of liability, indemnification, and
              dispute resolution — survive termination.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">16. Changes to these Terms</h2>
            <p>
              We may update these Terms as Stoop grows. If a change is material, we will provide notice (in the app or by
              email). Continued use after a change takes effect means you accept the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">17. General</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Entire agreement.</strong> These Terms, the Privacy Policy, and (for Makers) the Founding Maker
                Terms are the entire agreement between you and Stoop and supersede prior discussions.
              </li>
              <li>
                <strong>Severability and reformation.</strong> If any provision is unenforceable, it will be modified to
                the minimum extent needed to be enforceable, or severed, and the rest stays in effect.
              </li>
              <li>
                <strong>No waiver.</strong> Our failure to enforce a provision is not a waiver of it.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign these Terms; we may, to any successor, affiliate, or
                acquirer.
              </li>
              <li>
                <strong>Force majeure.</strong> Neither party is liable for delays caused by events beyond its reasonable
                control.
              </li>
              <li>
                <strong>Electronic records.</strong> You consent to receive records electronically, and your electronic
                acceptance has the same effect as a signature.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">18. Contact</h2>
            <p>
              Questions? Email{" "}
              <a href="mailto:hello@trystoop.com" className="underline">
                hello@trystoop.com
              </a>{" "}
              or use in-app support.
            </p>
          </section>

          <p className="text-sm text-muted-foreground">
            Makers: see also the{" "}
            <a href="/terms" className="underline">
              Founding Maker Terms
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
